import { Card, Row } from 'antd';
import gql from 'graphql-tag';
import React from 'react';
import { Query } from 'react-apollo';
import { Route, Switch } from 'react-router';
import { PaymentMethod } from 'smartpi-core';

import { ClockIn } from '../../types/clockIn';
import {
  GClockInActivityJoinedRecordWithStatus,
  UserActivityJoinedRecordWithStatus,
} from '../../types/marketingActivity';
import { SLMRelationship } from '../../types/slm';
import { UserAccount } from '../../types/userAccount';
import { Enrollment, Sku } from '../enrollment/Enrollment';
import { EnrollmentDetail } from '../enrollment/EnrollmentDetail';
import { LessonProgressDetail } from '../enrollment/LessonProgress';
import { UserRole } from '../rolePermission/type';
import { UserDetail } from './UserDetail';
import { UserList } from './UserList';

export const User = () => (
  <Row type="flex" justify="center">
    <Card>
      <Switch>
        <Route path="/user/list" component={UserList} />
        <Route path="/user/:id/enrollment/:enrollmentId/lesson/:serialId" component={LessonProgressDetail} />
        <Route path="/user/:id/enrollment/:enrollmentId" component={EnrollmentDetail} />
        <Route path="/user/:id" component={UserDetail} />
      </Switch>
    </Card>
  </Row>
);

export enum UserMetaType {
  REAL_NAME,
  REAL_GENDER,
  AGE,
  FAMILY_ROLE,
  REMARK,
  ADDRESS,
  REGISTER_CHANNEL,
  EMERGENCY_CONTACT,
}

export enum ChildPersonaType {
  GRADE,
  MATH_ABILITY,
  ENGLISH_ABILITY,
  CHINESE_ABILITY,
}

export enum ChildAbility {
  // Math
  MATH_NO_BASIC = 100000,
  MATH_KNOW_NUMBER_NO_ADDTION_OR_SUBTRACTION = 100100,
  MATH_ADDITION_WITHIN_5_NO_SUBTRACTION = 100200,
  MATH_ADDITION_AND_SUBTRACTION_WITHIN_5_VERY_SKILLED = 100300,
  MATH_ADDITION_WITHIN_10_NO_SUBTRACTION = 100400,
  MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_NEED_HAND_INDEX = 100500,
  MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_VERY_SKILLED_BUT_20_NOT_INCLUDED = 100600,
  MATH_SUBTRACTION_WITHIN_20 = 100700,
  MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_VERY_SKILLED = 100800,
  MATH_ADDITION_WITHIN_20_NO_SUBTRACTION = 100900,
  MATH_ADDITION_AND_SUBTRACTION_WITHIN_20_VERY_SKILLED = 101000,
  MATH_ADDITION_AND_SUBTRACTION_WITHIN_100_VERY_SKILLED = 101100,
  OTHER = 101200,
}

export interface User {
  id: number;
  name: string;
  mobile: string;
  gender: number;
  region: string;
  avatarUrl: string;
  avatarBase64: string;
  userMeta: UserMeta[];
  enrollments: Enrollment[];
  family: Family;
  orders: Order[];
  thirdPartyInfos: UserThirdPartyInfo[];
  roles: UserRole[];
  contactRecords: UserContactRecord[];
  inviter: User;
  invitees: User[];
}

export interface UserContactRecord {
  id: number;
  userId: number;
  feedback: UserContactRecordFeedback;
  isSuccess: boolean;
  type: UserContactRecordType;
  intent: UserContactRecordIntent;
  remark: string;
  createdAt: Date;
  teacherName: string;
}

export enum UserContactRecordFeedback {
  // 全阶段可用
  USER_BUSY_CONTACT_SOON = 1000, // 繁忙稍后再联系（不方便现在联系）
  CONTACT_NOT_SUCCESS, // 不接（一直响铃）
  CALL_REFUSED, // 拒接
  POWER_OFF, // 关机
  OUT_OF_SERVICE, // 停机
  NUMBER_NOT_EXIST, // 空号
  CONTACT_OTHERS = 1999, // 其他

  // -1周加好友阶段
  NOT_APPLIED_TRIAL_LESSON = 2000, // 没有报名体验课【不清楚情况】
  NO_NEED_OR_REQUIRE_REFUND_TRIAL_LESSON, // 不需要，要求退体验课
  REDEEM_TRIAL_LESSON_NOT_SUCCESS, // 体验课未兑换成功
  BOUGHT_TRIAL_LESSON_FOR_FRIEND, // 帮人购买体验课

  // 预热期/收定金期
  PRORDER_DONE = 3000, // 已收定金
  BOOK_FOR_BOOK_SUCCESS, // 绘本已登记
  NOT_PREORDER_BUT_POSITIVE_INTENT, // 有意向（但现在不登记/暂时不付定金）

  // 续费年课期
  NO_TIME_OR_CHILD_NOT_AROUND = 4000, // 不报名年课原因（没时间学习/孩子不在身边）
  HAS_OTHER_PRODUCT, // 不报名年课原因（已经在学习同类产品）
  LESSON_TOO_DIFFICULT, // 不报名年课原因（课程难）
  LESSON_TOO_EXPENSIVE, // 不报名年课原因（价格贵）
  FAMILY_DISAGREE, // 不报名年课原因（老公/老婆/家人不同意）
  CHILD_TAKE_BY_OLD, // 不报名年课原因（孩子老人带，没办法学习）
  CHILD_NOT_OLD_ENOUGH, // 不报名年课原因（孩子年纪小）
  WAITING_FOR_SALE, // 不报名年课原因（观望，等最后一天更大力度优惠）
  NOT_ENROLL_OTHERS = 4999, // 不报名年课原因（其他）

  // 成交年课
  ENROLL_ADVANCE_LESSON_SUCCESS = 5000, // 报名年课成功
  REDEEM_ADVANCE_LESSON_FAILED, // 年课未兑换成功
}

export enum UserContactRecordType {
  WECHAT,
  PHONE_CALL,
}

export enum UserContactRecordIntent {
  VERY_POSITIVE,
  POSITIVE,
  NEGATIVE,
  NO_RESPONSE,
}

export enum UserContactRecordStage {
  ALL_STAGES, // 全阶段
  PLUS_FRIENDS_PERIOD, // -1周加好友阶段
  PREHEAT_PERIOD, // 预热期/收定金期
  RENEW_YEAR_COURSE, // 续费年课期
  DEAL_YEAR_COURSE, // 成交年课
}

export const USER_CONTACT_RECORD_STAGE_LIST = {
  [UserContactRecordStage.ALL_STAGES]: '全阶段',
  [UserContactRecordStage.PLUS_FRIENDS_PERIOD]: '-1周加好友阶段',
  [UserContactRecordStage.PREHEAT_PERIOD]: '预热期/收定金期',
  [UserContactRecordStage.RENEW_YEAR_COURSE]: '续费年课期',
  [UserContactRecordStage.DEAL_YEAR_COURSE]: '成交年课',
};

export const USER_CONTACT_RECORD_FEEDBACK_LIST = {
  // 全阶段可用
  [UserContactRecordStage.ALL_STAGES]: {
    [UserContactRecordFeedback.USER_BUSY_CONTACT_SOON]: '繁忙稍后再联系（不方便现在联系）',
    [UserContactRecordFeedback.CONTACT_NOT_SUCCESS]: '不接（一直响铃）',
    [UserContactRecordFeedback.CALL_REFUSED]: '拒接',
    [UserContactRecordFeedback.POWER_OFF]: '关机',
    [UserContactRecordFeedback.OUT_OF_SERVICE]: '停机',
    [UserContactRecordFeedback.NUMBER_NOT_EXIST]: '空号',
    [UserContactRecordFeedback.CONTACT_OTHERS]: '其他',
  },

  // -1周加好友阶段
  [UserContactRecordStage.PLUS_FRIENDS_PERIOD]: {
    [UserContactRecordFeedback.NOT_APPLIED_TRIAL_LESSON]: '没有报名体验课【不清楚情况】',
    [UserContactRecordFeedback.NO_NEED_OR_REQUIRE_REFUND_TRIAL_LESSON]: '不需要，要求退体验课',
    [UserContactRecordFeedback.REDEEM_TRIAL_LESSON_NOT_SUCCESS]: '体验课未兑换成功',
    [UserContactRecordFeedback.BOUGHT_TRIAL_LESSON_FOR_FRIEND]: '帮人购买体验课',
  },

  // 预热期/收定金期
  [UserContactRecordStage.PREHEAT_PERIOD]: {
    [UserContactRecordFeedback.PRORDER_DONE]: '已收定金',
    [UserContactRecordFeedback.BOOK_FOR_BOOK_SUCCESS]: '绘本已登记',
    [UserContactRecordFeedback.NOT_PREORDER_BUT_POSITIVE_INTENT]: '有意向（但现在不登记/暂时不付定金）',
  },

  // 续费年课期
  [UserContactRecordStage.RENEW_YEAR_COURSE]: {
    [UserContactRecordFeedback.NO_TIME_OR_CHILD_NOT_AROUND]: '不报名年课原因（没时间学习/孩子不在身边）',
    [UserContactRecordFeedback.HAS_OTHER_PRODUCT]: '不报名年课原因（已经在学习同类产品）',
    [UserContactRecordFeedback.LESSON_TOO_DIFFICULT]: ' 不报名年课原因（课程难）',
    [UserContactRecordFeedback.LESSON_TOO_EXPENSIVE]: ' 不报名年课原因（价格贵）',
    [UserContactRecordFeedback.FAMILY_DISAGREE]: '不报名年课原因（老公/老婆/家人不同意）',
    [UserContactRecordFeedback.CHILD_TAKE_BY_OLD]: '不报名年课原因（孩子老人带，没办法学习）',
    [UserContactRecordFeedback.CHILD_NOT_OLD_ENOUGH]: '不报名年课原因（孩子年纪小）',
    [UserContactRecordFeedback.WAITING_FOR_SALE]: '不报名年课原因（观望，等最后一天更大力度优惠）',
    [UserContactRecordFeedback.NOT_ENROLL_OTHERS]: '不报名年课原因（其他）',
  },

  // 成交年课
  [UserContactRecordStage.DEAL_YEAR_COURSE]: {
    [UserContactRecordFeedback.ENROLL_ADVANCE_LESSON_SUCCESS]: '报名年课成功',
    [UserContactRecordFeedback.REDEEM_ADVANCE_LESSON_FAILED]: '年课未兑换成功',
  },
};

export const USER_CONTACT_RECORD_INTENT_NAME = {
  [UserContactRecordIntent.VERY_POSITIVE]: 'A',
  [UserContactRecordIntent.POSITIVE]: 'B',
  [UserContactRecordIntent.NEGATIVE]: 'C',
  [UserContactRecordIntent.NO_RESPONSE]: 'D',
};

export interface UserThirdPartyInfo {
  id: number;
  userId: number;
}

export interface GUser extends User {
  accounts: UserAccount[];
  clockInRecords: ClockIn[];
  enrollments: Enrollment[];
  orders: Order[];
  clockInActivityJoinedRecords: GClockInActivityJoinedRecordWithStatus[];
  slmActivityJoinedRecords: UserActivityJoinedRecordWithStatus[];
  slmRelationships: SLMRelationship[];
}

export interface Family {
  id: number;
  region: string;
  userId: number;
  children: Child[];
}

export interface UserMeta {
  id: number;

  userId: number;
  key: UserMetaType;
  value: string;
}

export interface Child {
  createdAt: string;
  id: number;
  name: string;
  gender: number;
  birthday: string;
  family: Family;
  familyId: number;
  userId: number;
  user: User;
  personas: ChildPersona[];
}

export interface ChildPersona {
  id: number;
  type: ChildPersonaType;
  value: number;
  child: Child;
  childId: number;
  customValue: string;
  createdAt: string;
}

export enum OrderStatus {
  PENDING,
  PAID,
  FULLFILLED,
  CANCELED,
  REFUNDED,
  REFUNDING,
  REFUNDFAILED,
}

export const OrderStatusName = {
  [OrderStatus.PENDING]: '待支付',
  [OrderStatus.PAID]: '已支付',
  [OrderStatus.FULLFILLED]: '已完成',
  [OrderStatus.CANCELED]: '已取消',
  [OrderStatus.REFUNDED]: '已退款',
  [OrderStatus.REFUNDING]: '退款中',
  [OrderStatus.REFUNDFAILED]: '退款失败',
};

export const KNOWLEDGE_POINT_LEVEL = [
  '零基础',
  '认识数字，但不会数数',
  '会数数，但不会加减',
  '会5以内实物加法，不会5以内实物减法',
  '会5以内实物加减法，不会10以内实物加减法',
  '会10以内实物加法，不会10以内实物减法',
  '会10以内实物加减法，不会10以内抽象加减法',
  '会10以内抽象加法，不会10以内抽象减法',
  '会10以内抽象加减法，不认识11~20的数',
  '会10以内抽象加减法，认识11~20的数，不会20以内加减法',
  '会20以内不进位加法， 不会20以内不退位减法',
  '会20以内不退位加减法，不会进退位加减法',
  '会20以内进位加法，不会20以内退位减法',
  '会20以内进退加减法',
];

export interface Order {
  id: number;
  status: OrderStatus;
  skus: Sku[];
  paymentInvoices: PaymentInvoices[];
  createdAt: string;
  updatedAt: string;
}

export enum PaymentInvoiceStatus {
  NEW,
  PENDING,
  CANCELLED,
  FAILED,
  COMPLETED,
}

export const PaymentInvoiceStatusName = {
  [PaymentInvoiceStatus.NEW]: '新',
  [PaymentInvoiceStatus.PENDING]: '进行中',
  [PaymentInvoiceStatus.CANCELLED]: '已取消',
  [PaymentInvoiceStatus.FAILED]: '失败',
  [PaymentInvoiceStatus.COMPLETED]: '已完成',
};

export interface PaymentInvoices {
  id: number;
  status: number;
  amount: number;
  paymentMethod: PaymentMethod;
  createdAt: string;
  updatedAt: string;
}

// 幼儿园：小班，中班，大班；小学：一年级、二年级、三年级；其他：托班，学前班
export enum Grade {
  // 托班 | 托儿所
  NURSERY = 1000,
  // 幼儿园 小班
  KINDERGARTEN_GRADE_1 = 2000,
  // 幼儿园 中班
  KINDERGARTEN_GRADE_2 = 2010,
  // 幼儿园 大班
  KINDERGARTEN_GRADE_3 = 2020,
  // 学前班
  PRESCHOOL = 3000,
  // 小学 一年级
  PRIMARY_SCHOOL_GRADE_1 = 4000,
  // 小学 二年级
  PRIMARY_SCHOOL_GRADE_2 = 4010,
  // 小学 三年级
  PRIMARY_SCHOOL_GRADE_3 = 4020,
}

export const GradeNames = {
  [Grade.NURSERY]: '托班',
  [Grade.KINDERGARTEN_GRADE_1]: '小班',
  [Grade.KINDERGARTEN_GRADE_2]: '中班',
  [Grade.KINDERGARTEN_GRADE_3]: '大班',
  [Grade.PRESCHOOL]: '学前班',
  [Grade.PRIMARY_SCHOOL_GRADE_1]: '小学一年级',
  [Grade.PRIMARY_SCHOOL_GRADE_2]: '小学二年级',
  [Grade.PRIMARY_SCHOOL_GRADE_3]: '小学三年级',
};

export const AbilityNames = {
  [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_100_VERY_SKILLED]: '100以内加减法都会',
  [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_NEED_HAND_INDEX]: '10以内加减法会,但需掰手指',
  [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_VERY_SKILLED]: '10以内加减法都会',
  [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_10_VERY_SKILLED_BUT_20_NOT_INCLUDED]:
    '10以内加减法都会,但不会20以内',
  [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_20_VERY_SKILLED]: '20以内加减法都会',
  [ChildAbility.MATH_ADDITION_AND_SUBTRACTION_WITHIN_5_VERY_SKILLED]: '5以内加减法都很熟练',
  [ChildAbility.MATH_ADDITION_WITHIN_10_NO_SUBTRACTION]: '10以内加法会，减法不会',
  [ChildAbility.MATH_ADDITION_WITHIN_20_NO_SUBTRACTION]: '20以内加法会，减法不会',
  [ChildAbility.MATH_KNOW_NUMBER_NO_ADDTION_OR_SUBTRACTION]: '只认识数字，但不会加减',
  [ChildAbility.MATH_NO_BASIC]: '零基础',
  [ChildAbility.MATH_SUBTRACTION_WITHIN_20]: '20以内减法会',
  [ChildAbility.OTHER]: '其他',
};

interface Variables {
  ids?: number[];
  mobile?: string;
  nickName?: string;
  miniGameName: string;
}
interface Data {
  users: GUser[];
}

export const ALL_USERS_QUERY = gql`
  query GetUser($ids: [ID], $mobile: String, $name: String, $miniGameName: String!) {
    users(ids: $ids, mobile: $mobile, name: $name) {
      id
      name
      mobile
      gender
      region
      avatarUrl
      coupons {
        id
        status
        validTo
        createdAt
        couponTemplateId
      }
      thirdPartyInfos {
        id
      }
      family {
        id
        children {
          id
          name
          gender
          birthday
          personas {
            id
            type
            value
            customValue
          }
        }
      }
      enrollments {
        id
        skuId
        sku {
          id
          name
          groups {
            id
            name
          }
          course {
            id
            name
            serialId
          }
          start
          end
          quizEndAt
        }
        miniGameResults(miniGameName: $miniGameName) {
          createdAt
          deletedAt
          id
          miniGameId
          result
          userId
          data {
            resultRecord
            knowledgeLevel
            level
            time
          }
        }
        group {
          id
          name
          teacher {
            id
            name
          }
        }
        lessonProgresses {
          serialId
          displayId
          status
          lesson {
            id
            type
            name
          }
        }
      }
      orders {
        id
        status
        createdAt
        total
        skus {
          id
          name
          course {
            id
            name
          }
        }
        paymentInvoices {
          id
          status
          amount
          paymentMethod
          createdAt
          updatedAt
        }
      }
      userMeta {
        id
        key
        userId
        value
      }
      inviter {
        id
        mobile
      }
      invitees {
        id
        mobile
      }
    }
  }
`;

export class AllUsers extends Query<Data, Variables> {}



// WEBPACK FOOTER //
// ./src/screens/user/Users.tsx